import { TFunction } from 'i18next';

import { CONSIGNMENT_STATUS, ConsignmentStatusCode } from '@/entities/consignments';
import type { SelectOption } from '@/shared/ui';

export const getMetricOptions = (t: TFunction): SelectOption[] => [
  { value: 'W', label: t('bundleType.bundleWeight.label', 'Weight') },
  { value: 'V', label: t('bundleType.bundleVolume.label', 'Volume') },
  { value: 'VA', label: t('requestline.insuranceValue.label', 'Total Value') },
  { value: 'C', label: t('cmrlist.title', 'Consignments') },
  { value: 'L', label: t('welcome.num.conslines.label', 'Number of consignment lines') },
  { value: 'D', label: t('tracking.servicereq.dangerous.label', 'Dangerous Goods') }
];

export const getGroupByOptions = (t: TFunction): SelectOption[] => [
  { value: 'D', label: t('gen.date.label', 'Date') },
  { value: 'W', label: t('gen.week.label', 'Week') },
  { value: 'L', label: t('location.name.label', 'Location') },
  { value: 'S', label: t('subregion.name.label', 'Subregion') },
  { value: 'R', label: t('region.name.label', 'Region') },
  { value: 'U', label: t('org.name.label', 'Service User') },
  { value: 'T', label: t('org.type.label', 'Service User Type') },
  { value: 'P', label: t('product.description.label', 'Product Category') }
];

export const getDateRangeOptions = (t: TFunction): SelectOption[] => [
  { value: 'LW', label: t('welcome.lastweek.label', 'Last week') },
  { value: 'L2W', label: t('welcome.last2weeks.label', 'Last 2 weeks') },
  { value: 'LM', label: t('welcome.lastmonth.label', 'Last month') },
  { value: 'L2M', label: t('welcome.last2month.label', 'Last 2 months') },
  { value: 'L6M', label: t('welcome.last6month.label', 'Last 6 months') },
  { value: 'LY', label: t('welcome.lastyear.label', 'Last year') },
  { value: 'FE', label: t('welcome.forever.label', 'All time') },
  { value: 'CR', label: t('welcome.customrange.label', 'Custom date range') }
];

export const getStatusOptions = (t: TFunction): SelectOption[] => [
  { value: 'DI', label: t('welcome.dispatched.label', 'Dispatched') },
  { value: 'RC', label: t('welcome.received.label', 'Received') }
];

export const getTransportGroupOptions = (t: TFunction): SelectOption[] => {
  const origin = t('welcome.origin.label', 'Origin');
  const destination = t('request.destinationLocation.label', 'Destination');
  const subregion = t('subregion.name.label', 'Subregion');
  const region = t('region.name.label', 'Region');
  const senderOrg = t('newcmr.senderorg.label', 'Sender Organization');

  return [
    { value: 'D', label: t('gen.date.label', 'Date') },
    { value: 'OL', label: t('journey.origin.label', 'Origin Location') },
    { value: 'OS', label: `${origin} ${subregion}` },
    { value: 'OR', label: `${origin} ${region}` },
    { value: 'DL', label: destination },
    { value: 'DS', label: `${destination} ${subregion}` },
    { value: 'DR', label: `${destination} ${region}` },
    { value: 'SO', label: t('org.name.label', 'Service User') },
    { value: 'SOT', label: `${senderOrg} ${t('org.orgType.label', 'Type')}` },
    { value: 'P', label: t('product.description.label', 'Product Category') }
  ];
};

/**
 * Break-down (stacking) dimension for the transport chart. Values and order
 * mirror the legacy JSF dropdown (`welcome.xhtml`, `data-opname="trmode"`) and
 * the API enum: `CO` groups by origin-destination pair (corridor), `CD` by
 * cargo condition (Lost / Sane / Damaged). The API has no commodity break-down.
 */
export const getBreakdownOptions = (t: TFunction): SelectOption[] => [
  { value: 'NO', label: t('gen.none.label', 'None') },
  { value: 'OL', label: t('journey.origin.label', 'Origin Location') },
  { value: 'DL', label: t('request.destinationLocation.label', 'Destination') },
  { value: 'CO', label: t('welcome.corridor.label', 'Corridor') },
  { value: 'CD', label: t('dispatch.condition.label', 'Condition') }
];

export const getConsignmentsStatusOptions = (t: TFunction): SelectOption<ConsignmentStatusCode | ''>[] => [
  { value: '', label: t('common.all', 'All') },
  { value: CONSIGNMENT_STATUS.AC, label: t('requestStatusName.AC', 'Accepted') },
  { value: CONSIGNMENT_STATUS.CA, label: t('requestStatusName.CA', 'Cancelled') },
  { value: CONSIGNMENT_STATUS.CL, label: t('requestStatusName.CL', 'Closed') },
  { value: CONSIGNMENT_STATUS.IP, label: t('requestStatusName.IP', 'In Progress') },
  { value: CONSIGNMENT_STATUS.NR, label: t('requestStatusName.NR', 'New') },
  { value: CONSIGNMENT_STATUS.OH, label: t('requestStatusName.OH', 'On Hold') }
];

export const getTransportModeOptions = (
  t: TFunction,
  vehicleCategories: { id: number; description: string }[] = []
): SelectOption[] => [
  { value: 'NO', label: t('gen.none.label', 'None') },
  ...vehicleCategories.map((vc) => ({ value: String(vc.id), label: t(`vehicleCategory.${vc.id}`, vc.description) }))
];
